import React, { useState } from 'react';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onLogin: (role: 'user' | 'admin') => void;
    onGuestCheckout?: (guestData: { name: string; email: string; address: string }) => void;
}

const AuthModal: React.FC<Props> = ({ isOpen, onClose, onLogin, onGuestCheckout }) => {
    const [isLogin, setIsLogin] = useState(true);
    const [isGuest, setIsGuest] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (isGuest && onGuestCheckout) {
            onGuestCheckout({ name, email, address });
        } else {
            onLogin(email.includes('admin') ? 'admin' : 'user');
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/40 backdrop-blur-md transition-opacity animate-fade-in"
                onClick={onClose}
            ></div>

            {/* Modal Container */}
            <div className="relative bg-white/90 dark:bg-surface-dark/95 backdrop-blur-xl w-full max-w-[480px] rounded-[2.5rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.3)] overflow-hidden border border-white/20 dark:border-gray-800 animate-scale-up-md">

                {/* Decorative Header */}
                <div className="relative h-48 bg-primary overflow-hidden">
                    <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
                    <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-primary-dark/30 rounded-full blur-2xl"></div>

                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-6">
                        <div className="w-16 h-16 rounded-[1.25rem] bg-white/20 backdrop-blur-lg flex items-center justify-center mb-4 border border-white/30 shadow-xl transition-transform hover:scale-110 duration-500">
                            <span className="material-icons-round text-3xl">pets</span>
                        </div>
                        <h2 className="text-2xl font-black tracking-tight leading-tight">
                            {isGuest ? 'Guest Checkout' : (isLogin ? 'Welcome Back!' : 'Join the Family')}
                        </h2>
                        <p className="text-xs font-bold opacity-80 uppercase tracking-widest mt-1">
                            {isGuest ? 'Quick & Easy Details' : (isLogin ? 'Experience premium pet care' : 'Sign up for a healthier pet')}
                        </p>
                    </div>

                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-6 right-6 w-10 h-10 rounded-full bg-black/10 hover:bg-black/20 backdrop-blur-md flex items-center justify-center text-white transition-all hover:rotate-90 z-20"
                    >
                        <span className="material-icons-round text-xl">close</span>
                    </button>
                </div>

                <div className="p-8 sm:p-10">
                    {/* Mode Switcher */}
                    {!isGuest && (
                        <div className="flex p-1.5 bg-gray-100 dark:bg-gray-800/50 rounded-2xl mb-10 relative">
                            <div
                                className={`absolute inset-y-1.5 w-[calc(50%-6px)] bg-white dark:bg-surface-dark rounded-xl shadow-lg transition-all duration-300 ease-out-back ${isLogin ? 'translate-x-0' : 'translate-x-[calc(100%+0px)]'}`}
                            />
                            <button
                                onClick={() => setIsLogin(true)}
                                className={`flex-1 py-3 text-sm font-black rounded-xl relative z-10 transition-colors ${isLogin ? 'text-primary' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                                Log In
                            </button>
                            <button
                                onClick={() => setIsLogin(false)}
                                className={`flex-1 py-3 text-sm font-black rounded-xl relative z-10 transition-colors ${!isLogin ? 'text-primary' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                                Sign Up
                            </button>
                        </div>
                    )}

                    {isGuest && (
                        <button
                            onClick={() => setIsGuest(false)}
                            className="mb-6 flex items-center gap-2 text-xs font-black uppercase tracking-widest text-gray-400 hover:text-primary transition-colors"
                        >
                            <span className="material-icons-round text-lg">arrow_back</span> Back to Login
                        </button>
                    )}

                    <form className="space-y-6" onSubmit={handleSubmit}>
                        {(!isLogin || isGuest) && (
                            <div className="space-y-2 group">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 transition-colors group-focus-within:text-primary">Full Name</label>
                                <div className="relative">
                                    <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg group-focus-within:text-primary transition-colors">person</span>
                                    <input
                                        required
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Your full name"
                                        className="w-full bg-gray-50 dark:bg-background-dark border-2 border-transparent focus:border-primary/20 focus:bg-white dark:focus:bg-surface-dark rounded-2xl pl-12 pr-4 py-4 font-bold transition-all outline-none"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="space-y-2 group">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 transition-colors group-focus-within:text-primary">Email Address</label>
                            <div className="relative">
                                <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg group-focus-within:text-primary transition-colors">alternate_email</span>
                                <input
                                    required
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@email.com"
                                    className="w-full bg-gray-50 dark:bg-background-dark border-2 border-transparent focus:border-primary/20 focus:bg-white dark:focus:bg-surface-dark rounded-2xl pl-12 pr-4 py-4 font-bold transition-all outline-none"
                                />
                            </div>
                        </div>

                        {isGuest && (
                            <div className="space-y-2 group">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 transition-colors group-focus-within:text-primary">Shipping Address</label>
                                <div className="relative">
                                    <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg group-focus-within:text-primary transition-colors">local_shipping</span>
                                    <input
                                        required
                                        type="text"
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        placeholder="Full address for delivery"
                                        className="w-full bg-gray-50 dark:bg-background-dark border-2 border-transparent focus:border-primary/20 focus:bg-white dark:focus:bg-surface-dark rounded-2xl pl-12 pr-4 py-4 font-bold transition-all outline-none"
                                    />
                                </div>
                            </div>
                        )}

                        {!isGuest && (
                            <div className="space-y-2 group">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 transition-colors group-focus-within:text-primary">Password</label>
                                <div className="relative">
                                    <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg group-focus-within:text-primary transition-colors">lock_person</span>
                                    <input
                                        required
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="w-full bg-gray-50 dark:bg-background-dark border-2 border-transparent focus:border-primary/20 focus:bg-white dark:focus:bg-surface-dark rounded-2xl pl-12 pr-4 py-4 font-bold transition-all outline-none"
                                    />
                                </div>
                            </div>
                        )}

                        {!isGuest && isLogin && (
                            <div className="flex justify-end -mt-2">
                                <button type="button" className="text-xs text-primary font-black hover:underline tracking-tight">Forgot Password?</button>
                            </div>
                        )}

                        <button type="submit" className="w-full bg-primary hover:bg-primary-dark text-white font-black py-5 rounded-2xl shadow-2xl shadow-primary/30 transition-all hover:scale-[1.02] active:scale-95 text-lg">
                            {isGuest ? 'Place Order as Guest' : (isLogin ? 'Log In to Account' : 'Create My Account')}
                        </button>
                    </form>

                    <div className="mt-8 space-y-6">
                        <div className="flex items-center gap-4 text-gray-300">
                            <div className="h-px flex-1 bg-current"></div>
                            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Or Continue with</span>
                            <div className="h-px flex-1 bg-current"></div>
                        </div>

                        {!isGuest && (
                            <div className="w-full">
                                <button
                                    onClick={() => setIsGuest(true)}
                                    className="w-full py-4 px-4 bg-gray-50 dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-750 transition-all text-xs font-black uppercase tracking-widest text-gray-500 hover:text-primary"
                                >
                                    Continue as Guest
                                </button>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <button className="flex items-center justify-center gap-3 py-3.5 px-4 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-750 transition-all group">
                                <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-5 h-5 group-hover:scale-110 transition-transform" alt="Google" />
                                <span className="text-sm font-black text-gray-600 dark:text-gray-300">Google</span>
                            </button>
                            <button className="flex items-center justify-center gap-3 py-3.5 px-4 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-750 transition-all group">
                                <img src="https://www.svgrepo.com/show/303108/apple-black-logo.svg" className="w-5 h-5 dark:invert group-hover:scale-110 transition-transform" alt="Apple" />
                                <span className="text-sm font-black text-gray-600 dark:text-gray-300">Apple</span>
                            </button>
                        </div>
                    </div>

                    <div className="mt-10 p-6 bg-gray-50 dark:bg-gray-800/50 rounded-3xl border border-gray-100 dark:border-gray-800">
                        <p className="text-[10px] text-center text-gray-400 font-black uppercase tracking-[0.2em] mb-4">Quick Demo Access</p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => onLogin('user')}
                                className="flex-1 py-2.5 px-2 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-500 hover:text-white transition-all shadow-sm"
                            >
                                User Demo
                            </button>
                            <button
                                onClick={() => onLogin('admin')}
                                className="flex-1 py-2.5 px-2 bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-purple-500 hover:text-white transition-all shadow-sm"
                            >
                                Admin Demo
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuthModal;
